/**
 * Seller Dashboard Component
 * Provides seller-focused market intelligence, listing price guidance,
 * and sell-readiness scoring — replacing traditional realtor CMA advice.
 */

import React, { useCallback, useState } from 'react';
import ListingForm from './ListingForm';
import MyListings from './MyListings';
import { JourneyDetailView } from '../TransactionJourney/TransactionJourney';
import { useHouseTransaction } from '../../agents/journeyAgent';
import APIAgent from '../../agents/apiAgent';
import './SellerDashboard.css';

function HouseJourneyView({ house, sellerId, onBack }) {
  const { transactionId, loading, error } = useHouseTransaction(house.id);

  return (
    <div className="seller-dashboard__journey-view">
      <button className="seller-dashboard__back-btn" onClick={onBack}>
        ← Back to My Listings
      </button>
      <h3 className="seller-dashboard__journey-title">{house.address}</h3>
      {loading && <p className="seller-dashboard__loading">Loading journey...</p>}
      {error && <p className="seller-dashboard__error">{error}</p>}
      {!loading && !error && !transactionId && (
        <p className="seller-dashboard__empty">
          No active transaction found for this listing yet. Once a buyer's offer is accepted and a transaction is created, the full journey will appear here.
        </p>
      )}
      {!loading && transactionId && (
        <JourneyDetailView
          transactionId={transactionId}
          viewerId={sellerId}
          viewerRole="seller"
          onBack={onBack}
        />
      )}
    </div>
  );
}

export default function SellerDashboard({
  sellerId,
  onNavigateToNegotiations,
  defaultView = 'my-listings',
  sharedListings,
  setSharedListings,
  // { houseId, step } — reopen that listing in the wizard at `step` (e.g.
  // 'review' after the DocuSign return). onResumeHandled clears it upstream.
  resumeListing = null,
  onResumeHandled,
  // Address from the public "start your listing" page, used once to prefill
  // Post Listing; onInitialListingUsed clears it upstream.
  initialListing = null,
  onInitialListingUsed,
}) {
  const [sellerView, setSellerView] = useState(defaultView);
  const [editingHouse, setEditingHouse] = useState(null);
  const [editInitialStep, setEditInitialStep] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [journeyHouse, setJourneyHouse] = useState(null);
  const [listingsRefreshToken, setListingsRefreshToken] = useState(0);

  const refreshMyListings = useCallback(() => {
    setListingsRefreshToken((token) => token + 1);
  }, []);

  const handleEditListing = useCallback(async (house, { initialStep = null } = {}) => {
    if (!house?.id) return;

    setEditLoading(true);
    setEditError(null);
    setEditInitialStep(initialStep);

    try {
      const fullHouse = await APIAgent.getHouseById(house.id);
      setEditingHouse(fullHouse || house);
      setSellerView('edit-listing');
    } catch (err) {
      console.error('Failed to load full listing for edit mode:', err);
      setEditError('Could not load complete listing details. Please try again.');
      setEditingHouse(house);
      setSellerView('edit-listing');
    } finally {
      setEditLoading(false);
    }
  }, []);

  // Sync internal view when the parent navigates between seller-listings / seller-post tabs.
  React.useEffect(() => {
    setSellerView(defaultView);
  }, [defaultView]);

  React.useEffect(() => {
    if (!resumeListing?.houseId) return;
    handleEditListing({ id: resumeListing.houseId }, { initialStep: resumeListing.step });
    if (typeof onResumeHandled === 'function') onResumeHandled();
  }, [resumeListing, handleEditListing, onResumeHandled]);

  const closeEditor = () => {
    setEditingHouse(null);
    setEditInitialStep(null);
    setEditError(null);
    setSellerView('my-listings');
  };

  return (
    <div className="seller-dashboard">
      <div className="seller-dashboard__header">
        <div className="seller-dashboard__title-group">
          <h2 className="seller-dashboard__title">Sell My Home</h2>
          <p className="seller-dashboard__subtitle">
            AI-powered market intelligence — Cut the 6%
          </p>
        </div>
      </div>

      {/* Top-level Seller Workflow Tabs */}
      <div className="seller-workflow-tabs">
        <button
          className={`seller-workflow-tabs__btn ${sellerView === 'my-listings' ? 'active' : ''}`}
          onClick={() => setSellerView('my-listings')}
        >
          My Listings
        </button>
        <button
          className={`seller-workflow-tabs__btn ${sellerView === 'post-listing' ? 'active' : ''}`}
          onClick={() => setSellerView('post-listing')}
        >
          Post a Listing
        </button>
      </div>

      {/* My Listings View */}
      {sellerView === 'my-listings' && !journeyHouse && (
        <MyListings
          sellerId={sellerId}
          onCreateNew={() => setSellerView('post-listing')}
          onEditListing={handleEditListing}
          onNavigateToNegotiations={onNavigateToNegotiations}
          onViewJourney={(house) => setJourneyHouse(house)}
          sharedListings={sharedListings}
          setSharedListings={setSharedListings}
          refreshToken={listingsRefreshToken}
        />
      )}

      {sellerView === 'my-listings' && editError && (
        <p className="seller-dashboard__error" role="alert">{editError}</p>
      )}

      {/* House Journey View */}
      {sellerView === 'my-listings' && journeyHouse && (
        <HouseJourneyView
          house={journeyHouse}
          sellerId={sellerId}
          onBack={() => setJourneyHouse(null)}
        />
      )}

      {/* Post a Listing View */}
      {sellerView === 'post-listing' && (
        <ListingForm
          sellerId={sellerId}
          initialValues={initialListing}
          onInitialValuesUsed={onInitialListingUsed}
          onListingCreated={() => setSellerView('my-listings')}
          onListingSaved={refreshMyListings}
          onCancel={() => setSellerView('my-listings')}
        />
      )}

      {/* Edit a Draft Listing View */}
      {sellerView === 'edit-listing' && editingHouse && (
        <ListingForm
          sellerId={sellerId}
          editHouse={editingHouse}
          initialStep={editInitialStep}
          onListingCreated={closeEditor}
          onListingSaved={refreshMyListings}
          onCancel={closeEditor}
        />
      )}

      {sellerView === 'edit-listing' && editLoading && (
        <p className="seller-dashboard__loading">Loading listing details...</p>
      )}

    </div>
  );
}
